import React from 'react';

import { Button, Result } from 'antd';
import AppContext from 'antd/es/app/context';

import type { predicate } from '@code-expert/prelude';
import {
  array,
  constVoid,
  fn,
  fromThrown,
  pipe,
  record,
  string,
  tagged,
} from '@code-expert/prelude';
import { Logger } from '/imports/modules/Logger/client';
import { isSupportedBrowser } from '/imports/ui/components/BrowserUpdate';
import { ToggleDrawer } from '/imports/ui/components/ToggleDrawer';
import { DocumentTitle } from '/imports/ui/hooks/useTitle';
import { SendMessage } from './Messages/SendMessage';

const { userAgent } = navigator;

type State =
  tagged.Tagged<'ok'> | tagged.Tagged<'error', unknown> | tagged.Tagged<'asyncError', unknown>;

const stateAdt = tagged.build<State>();

// Copied from https://github.com/storybookjs/storybook/blob/next/code/core/src/manager/utils/prepareForTelemetry.ts
const ignoredBrowserErrors = [
  // It's a harmless issue with react-resize-detector that supposedly will be gone when we move to React 18.
  // https://github.com/maslianok/react-resize-detector/issues/45#issuecomment-1500958024
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
  // Safari does not seem to provide any helpful info on window.onerror
  // https://bugs.webkit.org/show_bug.cgi?id=132945
  'Script error.',
  // When react-dev-tools is installed as a browser extension, it will log this error
  'React is running in production mode',
  // This is a problem with Brave on iOS that we can ignore
  // https://community.brave.app/t/ios-window-ethereum-selectedwallet-undefined/659547
  "TypeError: undefined is not an object (evaluating 'window.ethereum.selectedAddress = undefined')",
];

const ignoreBrowserError: predicate.Predicate<string> = (message) =>
  pipe(ignoredBrowserErrors, array.elem(string.Eq)(message));

/**
 * We want to avoid logging errors that did not originate in our own application. This function
 * tests for known foreign errors that should not be logged.
 */
const isForeignError = (error: Error): boolean =>
  // The error originated in a Chrome browser extension (cx-3656)
  !!error.stack?.includes('chrome-extension://') ||
  // Likely caused by an extension
  error.message.includes('change_ua') ||
  // This error is likely from the MetaMask browser extension (cx-3656)
  ('code' in error && error.code === 4001);

/**
 * Class ErrorBoundary
 *
 * According to https://reactjs.org/docs/react-component.html#error-boundaries
 */
export class ErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state = stateAdt.ok();

  static contextType = AppContext;

  declare context: React.ContextType<typeof AppContext>;

  static getDerivedStateFromError(error: unknown) {
    // Update state displaying the fallback UI
    return stateAdt.error(error);
  }

  private logUnhandledError = (message: string, error: Error | null, extra?: object): void => {
    const args = fn.tuple(message, {
      ...(error == null ? undefined : { error }),
      ...extra,
      userAgent,
    });
    if (
      stateAdt.is.ok(this.state) &&
      isSupportedBrowser() &&
      (error == null || !isForeignError(error))
    ) {
      Logger.persist.error(...args);
    } else {
      Logger.error(...args);
    }
  };

  private unhandledErrorHandler = ({ error, message, filename, lineno, colno }: ErrorEvent) => {
    const ignore = ignoreBrowserError(message);
    const err = error == null ? null : fromThrown(error);
    const hasSource = !string.isBlank(filename);
    if (err != null || !ignore || hasSource) {
      const source = () => `${filename}:${lineno}:${colno}`;
      const extra = { message, ...(hasSource ? { source: source() } : undefined) };
      this.logUnhandledError('ErrorBoundary: Uncaught error', err, extra);
    }
    if (!ignore && stateAdt.is.ok(this.state)) {
      this.setState(stateAdt.error(err));
    }
  };

  private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
    const error = fromThrown(event.reason);
    this.logUnhandledError('ErrorBoundary: Unhandled promise rejection', error);
    if (stateAdt.is.ok(this.state)) {
      this.setState(stateAdt.asyncError(error));
    }
  };

  componentDidMount() {
    window.addEventListener('error', this.unhandledErrorHandler);
    window.addEventListener('unhandledrejection', this.promiseRejectionHandler);
  }

  componentDidUpdate() {
    stateAdt.fold(this.state, {
      ok: constVoid,
      error: constVoid,
      asyncError: (error) => {
        const msg =
          error instanceof Error
            ? error.message
            : string.isString(error)
              ? error
              : 'An unknown error occurred';
        void this.context.message.error(msg);
        this.setState(stateAdt.ok());
      },
    });
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.unhandledErrorHandler);
    window.removeEventListener('unhandledrejection', this.promiseRejectionHandler);
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Logger.persist.error('ErrorBoundary: Component error', {
      error,
      componentStack: errorInfo.componentStack ?? null,
      userAgent,
    });
  }

  render() {
    return stateAdt.fold(this.state, {
      ok: () => <>{this.props.children}</>,
      asyncError: () => <>{this.props.children}</>,
      error: (error) =>
        isSupportedBrowser() ? (
          <>
            <DocumentTitle title="Error" />
            <Result
              status="error"
              title="Code Expert crashed"
              subTitle="Please reload the page and try again. If this error persists, please get in touch."
              extra={[
                <ToggleDrawer key="SendMessage">
                  {({ open, setOpen }) => (
                    <>
                      <Button onClick={() => setOpen(true)}>
                        Send error to the Code Expert team
                      </Button>
                      <SendMessage
                        hideCourse
                        defaultRequest="codeExpert"
                        additionalText={formatError(error)}
                        open={open}
                        setOpen={setOpen}
                      />
                    </>
                  )}
                </ToggleDrawer>,
              ]}
            />
          </>
        ) : (
          <>
            <DocumentTitle title="Unsupported browser" />
            <Result
              status="500"
              title="Unsupported browser"
              subTitle="Code Expert only works with recent browsers."
              extra={
                <Button type="primary" href="https://browser-update.org/update-browser.html">
                  Update your browser
                </Button>
              }
            />
          </>
        ),
    });
  }
}

const safeStringify = (o: object): string => {
  try {
    return JSON.stringify(o);
  } catch {
    return o.toString();
  }
};

const formatError = (error: unknown): string => {
  if (error instanceof Error) {
    const causeStr = 'cause' in error ? `\n\nCaused by:\n${formatError(error.cause)}` : '';
    return `[${error.name}] ${error.message}${causeStr}`;
  }
  const str = record.isObject(error) ? safeStringify(error) : String(error);
  return `[error of type ${typeof error}] ${str}`;
};
